export interface ReadCacheScope {
	tenantId: string;
	userId: string;
}

export interface ReadCacheKeyInput {
	operationName: string | undefined;
	scope: ReadCacheScope;
	variables?: unknown;
}

export const READ_CACHE_KEY_PREFIX = "/graphql/__q";

const TENANT_SEGMENT = 0;
const OPERATION_NAME_SEGMENT = 2;
const READ_CACHE_KEY_SEGMENT_COUNT = 4;

const FNV_PRIME = 0x01000193;
const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_ALTERNATE_BASIS = 0x9dc5811c;
const HEX_WIDTH = 8;

const byKey = ([left]: [string, unknown], [right]: [string, unknown]): number => {
	if (left === right) return 0;
	return left < right ? -1 : 1;
};

export const stableStringify = (value: unknown): string => {
	if (value === undefined) return "undefined";
	if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
	if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
	const definedEntries = Object.entries(value as Record<string, unknown>).filter(([, entry]) => entry !== undefined);
	const serialized = definedEntries
		.sort(byKey)
		.map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`);
	return `{${serialized.join(",")}}`;
};

const fnv1a = (input: string, basis: number): number => {
	let hash = basis;
	for (let index = 0; index < input.length; index += 1) {
		hash ^= input.charCodeAt(index);
		hash = Math.imul(hash, FNV_PRIME);
	}
	return hash >>> 0;
};

const toHex = (value: number): string => value.toString(16).padStart(HEX_WIDTH, "0");

export const stableHash = (value: unknown): string => {
	const serialized = stableStringify(value);
	return `${toHex(fnv1a(serialized, FNV_OFFSET_BASIS))}${toHex(fnv1a(serialized, FNV_ALTERNATE_BASIS))}`;
};

const readCacheKeySegments = (key: string | null | undefined): string[] | undefined => {
	if (!key?.startsWith(`${READ_CACHE_KEY_PREFIX}/`)) return undefined;
	const segments = key.slice(READ_CACHE_KEY_PREFIX.length + 1).split("/");
	if (segments.length !== READ_CACHE_KEY_SEGMENT_COUNT) return undefined;
	return segments.some((segment) => segment.length === 0) ? undefined : segments;
};

const decodeSegment = (segment: string): string | undefined => {
	try {
		return decodeURIComponent(segment);
	} catch {
		return undefined;
	}
};

export const readOperationNameFromCacheKey = (key: string | null | undefined): string | undefined => {
	const segments = readCacheKeySegments(key);
	return segments === undefined ? undefined : decodeSegment(segments[OPERATION_NAME_SEGMENT]);
};

export const readTenantIdFromCacheKey = (key: string | null | undefined): string | undefined => {
	const segments = readCacheKeySegments(key);
	return segments === undefined ? undefined : decodeSegment(segments[TENANT_SEGMENT]);
};

const buildReadCacheKey = ({ operationName, scope, variables }: ReadCacheKeyInput): string | undefined => {
	if (!operationName || !scope.tenantId || !scope.userId) return undefined;
	const partition = [scope.tenantId, scope.userId, operationName].map(encodeURIComponent).join("/");
	return `${READ_CACHE_KEY_PREFIX}/${partition}/${stableHash(variables)}`;
};

export default buildReadCacheKey;
